import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { DateFieldContainer } from '@/components/inputs/DateFieldContainer'
import type { SpouseAccordionViewModel } from './SpouseAccordionViewModel'
import { useId } from 'react'
import { FormContainer } from '@/components/FormContainer'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetLegend,
} from '@/components/Form'
import { masks } from '@/utils/input-mask-util'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { StepCardListItemContainer } from '../../../components/StepCardListItemContainer'

interface SpouseAccordionProps {
  vm: SpouseAccordionViewModel
  onSetAsTaxpayer: () => void
  testId?: string
}

export const SpouseAccordion = observer(function SpouseAccordion({
  vm,
  onSetAsTaxpayer,
  testId,
}: SpouseAccordionProps) {
  const formId = useId()
  return (
    <StepCardListItemContainer
      icon="gem"
      title={vm.fullName}
      subtitle="Spouse"
      extras={
        <Button
          variant="text"
          label="Set as Taxpayer"
          iconLeft="arrow-right-left"
          onPress={onSetAsTaxpayer}
          testId={testId ? `${testId}-set-as-taxpayer-button` : undefined}
        />
      }
      isExpanded={vm.isExpanded}
      onExpandedChange={vm.toggleExpanded}
      testId={testId}
    >
      <FormContainer id={formId}>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="given-name"
            model={vm}
            field="firstName"
            label={<FormFieldRequiredLabel label="First name" />}
            placeholder="e.g. Jane"
            required
            testId={testId ? `${testId}-first-name-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="additional-name"
            model={vm}
            field="middleInitial"
            label="M.I."
            placeholder="e.g. M"
            testId={testId ? `${testId}-middle-initial-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="family-name"
            model={vm}
            field="lastName"
            label={<FormFieldRequiredLabel label="Last name" />}
            placeholder="e.g. Doe"
            required
            testId={testId ? `${testId}-last-name-input` : undefined}
          />
        </FormFieldGroup>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="off"
            model={vm}
            field="tin"
            label={<FormFieldRequiredLabel label="SSN" />}
            placeholder="e.g. 123-45-6789"
            mask={masks.ssnOrItin}
            required
            data-private
            testId={testId ? `${testId}-tin-input` : undefined}
          />
          <DateFieldContainer
            model={vm}
            field="dateOfBirth"
            label={<FormFieldRequiredLabel label="Date of birth" />}
            placeholder="MM/DD/YYYY"
            required
            data-private
            testId={testId ? `${testId}-date-of-birth-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="occupation"
            label="Occupation"
            placeholder="e.g. School Teacher"
            testId={testId ? `${testId}-occupation-input` : undefined}
          />
        </FormFieldGroup>
        <FormFieldSet>
          <FormFieldSetLegend>Contact Info</FormFieldSetLegend>
          <FormFieldGroup stackWhenCompact>
            <TextFieldContainer
              autoComplete="email"
              model={vm}
              field="emailAddress"
              label="Email address"
              placeholder="e.g. jane@example.com"
              testId={testId ? `${testId}-email-input` : undefined}
            />
            <TextFieldContainer
              autoComplete="tel"
              type="tel"
              model={vm}
              field="phoneNumber"
              label="Phone number"
              placeholder="e.g. 305-123-4567"
              testId={testId ? `${testId}-phone-number-input` : undefined}
            />
          </FormFieldGroup>
        </FormFieldSet>
      </FormContainer>
    </StepCardListItemContainer>
  )
})
